import { useEffect, useRef, useState, useMemo } from 'react';
import { Player } from '@lordicon/react';
import { Container, Row, Col, Button } from 'reactstrap';
import {
  useToast,
  useTranslator,
  cloudflareImage,
  mealPlaceholderImage,
  useAppContext,
} from '@front-activerse/common-components';

import { useOutletContext } from 'react-router-dom';
import DayPlanNutritions from '../../MealPlan/MealView/components/DayPlanNutritions';
import DayPlanMealRating from '../../MealPlan/MealView/components/DayPlanMealRating';
import InstacartButton from '../../MealPlan/MealView/components/InstacartButton';
import FriscoButton from '../../MealPlan/MealView/components/FriscoButton';

import { useNavigate } from 'react-router-dom';

const FavMealDetails = ({
  meal,
  children,
  toggleInsertDate,
  removeMealFromFavorites,
  addMealToFavorites,
  rateMeal,
  refetch,
}) => {
  const videoRef = useRef();
  const ratingRef = useRef();

  const playerRefHeart = useRef(null);
  const iconsIconFgColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-fg-color')
      .trim();
    return color || '#000000';
  }, []);

  const { t } = useTranslator();
  const { toastError, toastSuccess } = useToast();
  const { trackFirebaseEvent } = useOutletContext();
  const navigate = useNavigate();

  const [visiblePoster, setVisiblePoster] = useState(true);

  const {
    consts: { APP_BRAND, FRISCO },
    images: { playBtn, addToMealplan, addToTrackerIcon },
    lotties: { heartFullIcon },
  } = useAppContext();

  useEffect(() => {
    trackFirebaseEvent('screen_view ', {
      firebase_screen: 'Favourite_details',
      meal_id: meal.id,
      favourites_category: meal.mealType.name,
    });
  }, []);

  const calculatePreparationTime = (minutes) => {
    const steps = [5, 15, 30, 45, 60];

    if (minutes === 0 || minutes == null) {
      return null;
    }

    for (const step of steps) {
      if (minutes <= step) {
        return `< ${step} min`;
      }
    }
    return `> ${steps[steps.length - 1]} min`;
  };

  const handleRemoveMeal = async () => {
    try {
      const response = await removeMealFromFavorites.removeMeal(meal.id);
      if (response.data.me.removeMealFromFavoritesByMealId.__typename === 'BasicMutationSuccess') {
        toastSuccess(
          <p>
            {t('mealPlan.favorites.notification.removedFromFav.content')}
            <Button color="link" onClick={() => handleAddMeal(meal.id)}>
              {t('meal.favorites.notification.removedFromFav.button')}
            </Button>
          </p>
        );
        navigate(-1);
      }
    } catch (error) {
      toastError();

      throw new Error(`Failed to remove meal from favorites: ${error}`);
    }
  };

  const handleAddMeal = async (mealId) => {
    try {
      await addMealToFavorites.addMeal(mealId);
    } catch (error) {
      toastError();

      throw new Error(`Failed to add meal to favorites: ${error}`);
    }
  };

  const renderMealVideo = () => {
    if (!meal.preparationVideoUrl) {
      return renderMealImage();
    }

    return (
      <div className="embed-responsive ratio ratio-1x1">
        <video
          ref={videoRef}
          src={meal.preparationVideoUrl}
          controls
          playsInline
          controlsList="nodownload"
          disablePictureInPicture
          onEnded={() => setVisiblePoster(true)}
        />
        {visiblePoster ? (
          <div
            style={{
              backgroundImage: `url(${meal.preparationImageUrl
                .replace('(', '%28')
                .replace(')', '%29')})`,
            }}
            className="embed-responsive-item d-flex align-items-center justify-content-center"
            onClick={() => playVideo(videoRef)}
          >
            <img src={playBtn} alt="" />
          </div>
        ) : (
          ''
        )}
      </div>
    );
  };

  const renderMealImage = () => {
    let mealImg;
    if (meal.preparationImageUrl) {
      mealImg = meal.preparationImageUrl;
    } else {
      mealImg = mealPlaceholderImage(meal.mealType, APP_BRAND);
    }

    const coverImage = cloudflareImage(mealImg, `${APP_BRAND}`, 'mealPlanMealDetails');

    return <img src={coverImage} className="img-fluid" width={768} height={768} alt="" />;
  };

  const playVideo = async (myVideoRef) => {
    setVisiblePoster(false);

    trackFirebaseEvent('user_action', {
      action: 'tap-watch_video',
      location: 'Meal_details',
      meal_id: meal.id,
      meal_category: meal.mealType.name,
    });

    try {
      await myVideoRef.current.play();
    } catch (err) {
      toastError();
      console.warn(err);
    }
  };

  const renderTags = () => {
    if (meal.mealCustomTag && meal.mealCustomTag.label) {
      return (
        <div className="tags">
          {meal.mealCustomTag && (
            <span
              className="tag me-1"
              style={{ backgroundColor: meal.mealCustomTag.color, color: '#fff' }}
            >
              {meal.mealCustomTag.label}
            </span>
          )}
          <span className="tag me-1">{meal.name}</span>
        </div>
      );
    }
    return (
      <div className="tags">
        <span className="tag me-1">{meal.name}</span>
      </div>
    );
  };

  const handleAddMealToTracker = () => {
    navigate(`/meals/day-plan/module/tracker/add-fav-meal/${meal.id}`);
  };

  const calculatedPreparationTime = calculatePreparationTime(meal.preparationTime.minutes);

  return (
    <>
      <Container className="py-md-4">
        <Row>
          <Col xl={{ size: 10, offset: 1 }}>
            <div className="page-container">
              <div className="meal-details">
                <Row>
                  <Col md="6">
                    <div className="meal-image-video">
                      {renderMealVideo()}

                      <Button
                        className="add-remove-fav"
                        color="link"
                        onClick={handleRemoveMeal}
                        onMouseEnter={() => playerRefHeart.current?.playFromBeginning()}
                        onTouchStart={() => playerRefHeart.current?.playFromBeginning()}
                      >
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            backgroundColor: 'rgba(0, 0, 0, 0.25)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                          }}
                        >
                          <Player
                            ref={playerRefHeart}
                            icon={heartFullIcon}
                            size={22}
                            colorize={iconsIconFgColor}
                          />
                        </div>
                      </Button>

                      {visiblePoster && (
                        <div className="meal-info2 d-md-none">
                          <Container>
                            {renderTags()}
                            <h1 className="meal-title title-large semi-bold white mb-0">
                              {meal.publicName}
                            </h1>
                          </Container>
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="instacart-container d-md-none">
                      <InstacartButton
                        meal={meal}
                        publicName={meal.publicName}
                        preparationImageUrl={meal.preparationImageUrl}
                      />
                      {FRISCO && <FriscoButton meal={meal} publicName={meal.publicName} />}
                    </div>

                    <div className="meal-info">
                      <div className="d-none d-md-block">
                        {renderTags()}
                        <h1 className="meal-title title-large semi-bold mb-xl">
                          {meal.publicName}
                        </h1>
                      </div>

                      <DayPlanMealRating
                        rateMeal={rateMeal}
                        refetch={refetch}
                        meal={meal}
                        reference={ratingRef}
                      />

                      {calculatedPreparationTime && (
                        <div className="grey-box mb-2">
                          {t('mealDetails.info.prepTime.title')}{' '}
                          <strong className="float-end">{calculatedPreparationTime}</strong>
                        </div>
                      )}

                      <div className="d-none d-md-block">
                        <DayPlanNutritions kcal={meal.kcal} macro={meal.macro} />
                      </div>

                      <div className="action-buttons d-none d-md-block">
                        <Row className="gx-0">
                          <Col xs={6}>
                            <Button
                              color="link"
                              size="sm"
                              className="w-100"
                              onClick={() => toggleInsertDate(meal.id)}
                            >
                              <img src={addToMealplan} alt="" className="me-2" />
                              {t('meal.favorites.mealDetails.addMealToMenu.button')}
                            </Button>
                          </Col>
                          <Col xs={6}>
                            <Button
                              color="primary"
                              size="sm"
                              className="w-100 px-3 tracker"
                              onClick={handleAddMealToTracker}
                            >
                              <img
                                src={addToTrackerIcon}
                                alt={t('tracker.meal.addToTrackerButton')}
                                className="me-2"
                              />
                              {t('tracker.meal.addToTrackerButton')}
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      <div className="d-none d-md-block mt-xl">
                        <InstacartButton
                          meal={meal}
                          publicName={meal.publicName}
                          preparationImageUrl={meal.preparationImageUrl}
                        />
                        <FriscoButton meal={meal} publicName={meal.publicName} />
                      </div>
                    </div>
                  </Col>
                </Row>

                {children()}

                <div className="d-md-none">
                  <DayPlanNutritions kcal={meal.kcal} macro={meal.macro} />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <footer className="d-md-none">
        <Container className="px-3">
          <Row className="gx-0">
            <Col xs={5}>
              <Button
                color="link"
                size="sm"
                className="w-100 ps-0"
                onClick={() => toggleInsertDate(meal.id)}
              >
                <img src={addToMealplan} alt="" className="me-2" />
                {t('meal.favorites.mealDetails.addMealToMenu.button')}
              </Button>
            </Col>
            <Col xs={7}>
              <Button
                color="primary"
                size="sm"
                className="w-100 px-3 tracker"
                onClick={handleAddMealToTracker}
              >
                <img
                  src={addToTrackerIcon}
                  alt={t('tracker.meal.addToTrackerButton')}
                  className="me-2"
                />
                {t('tracker.meal.addToTrackerButton')}
              </Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </>
  );
};

export default FavMealDetails;
